import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { CustomerTheme, getThemeBySubdomain, getThemeByDomain, getCustomerTheme } from '../lib/themes';
import {
  getOrganizationType,
  getCustomerSubdomain,
} from '../lib/organizationType';

interface ThemeContextType {
  theme: CustomerTheme;
  customerId: string;
  updateTheme: (customerId: string) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [currentTheme, setCurrentTheme] = useState<CustomerTheme>(() => {
    const hostname = window.location.hostname;
    // Use domain-based theme so *.localhost (e.g. synetic.localhost when impersonating) gets correct theme
    return getThemeByDomain(hostname);
  });

  const [customerId, setCustomerId] = useState<string>(() => {
    const sub = getCustomerSubdomain();
    return sub || 'default';
  });

  useEffect(() => {
    const hostname = window.location.hostname;
    console.log('ThemeContext: useEffect running for hostname:', hostname);

    const organizationType = getOrganizationType();
    const customerSubdomain = getCustomerSubdomain();

    if (organizationType === 'assettech') {
      // For Assettech organizations, use the assettech theme
      const theme = getCustomerTheme('assettech');
      setCurrentTheme(theme);
      setCustomerId(customerSubdomain || 'assettech');
    } else {
      // For other organizations, use subdomain-based theme selection
      if (customerSubdomain) {
        const theme = getThemeBySubdomain(customerSubdomain);
        setCurrentTheme(theme);
        setCustomerId(customerSubdomain);
      }
    }
  }, []);

  const updateTheme = (newCustomerId: string) => {
    console.log('ThemeContext: Updating theme to:', newCustomerId);
    const theme = getCustomerTheme(newCustomerId);
    setCurrentTheme(theme);
    setCustomerId(newCustomerId);
  };

  const value: ThemeContextType = {
    theme: currentTheme,
    customerId,
    updateTheme,
  };

  console.log('ThemeContext: Current theme:', currentTheme.customer.name, 'Customer ID:', customerId);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
